import api from '../lib/api';
import type {
  CategoryListItem,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  BrandListItem,
  CreateBrandRequest,
  UpdateBrandRequest,
  AttributeListItem,
  AttributeDetail,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  AddAttributeValueRequest,
  AttributeValueItem,
  AttributeValuesResponse,
  AttributeValuesQuery,
} from '../types/admin';
import type { PagedResponse, PageQuery } from '../types/paging';

const tenantBase = (tenantId: string) => `/api/v1/tenants/${tenantId}`;

// ── Typed query-key factories ─────────────────────────────────────────────────

export const catalogKeys = {
  categorySearch: (tenantId: string | undefined, q: string) =>
    ['catalog', 'categories', 'search', tenantId, q] as const,
  brandSearch: (tenantId: string | undefined, q: string) =>
    ['catalog', 'brands', 'search', tenantId, q] as const,
  marketplaceAttrValues: (tenantId: string, productId: string, catAttrId: string, q: string, limit: number) =>
    ['marketplace-attr-values', tenantId, productId, catAttrId, q, limit] as const,
};

const pagedParams = (opts?: PageQuery) => {
  const params: Record<string, string | number> = {};
  if (opts?.q) params.q = opts.q;
  if (opts?.page && opts.page > 0) params.page = opts.page;
  if (opts?.pageSize && opts.pageSize > 0) params.pageSize = opts.pageSize;
  return params;
};

/** Backend henüz paged shape'e migrate olmayan bir endpoint dizi dönerse de yaşayabilelim. */
const normalizePaged = <T>(data: PagedResponse<T> | T[] | null | undefined, page: number, pageSize: number): PagedResponse<T> => {
  if (Array.isArray(data)) return { total: data.length, page, pageSize, items: data };
  return {
    total: data?.total ?? 0,
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    items: data?.items ?? [],
  };
};

/** Tenant catalog API servisi — kategoriler, markalar, nitelikler */
export const catalogApi = {
  // ── Categories ───────────────────────────────────────────────────
  /** Tenant kategorilerini sayfalı döner */
  getCategoriesPaged: (tenantId: string, opts?: PageQuery, signal?: AbortSignal) =>
    api
      .get<PagedResponse<CategoryListItem> | CategoryListItem[]>(`${tenantBase(tenantId)}/categories`, { params: pagedParams(opts), signal })
      .then((r) => normalizePaged<CategoryListItem>(r.data, opts?.page ?? 1, opts?.pageSize ?? 100)),

  /** Dropdown/lookup için — tüm kategorileri tek seferde (max 1000) */
  getAllCategories: (tenantId: string, signal?: AbortSignal) =>
    api
      .get<PagedResponse<CategoryListItem> | CategoryListItem[]>(`${tenantBase(tenantId)}/categories`, { params: { pageSize: 1000 }, signal })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  /** Server-side arama — debounced input için, max 50 sonuç */
  searchCategories: (tenantId: string, q: string, signal?: AbortSignal) =>
    api
      .get<PagedResponse<CategoryListItem> | CategoryListItem[]>(`${tenantBase(tenantId)}/categories`, {
        params: { q: q.trim() || undefined, pageSize: 50, page: 1 },
        signal,
      })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  /** Tek kategori detayı */
  getCategory: (tenantId: string, id: string) =>
    api.get<CategoryListItem>(`${tenantBase(tenantId)}/categories/${id}`).then((r) => r.data),

  createCategory: (tenantId: string, data: CreateCategoryRequest) =>
    api.post<CategoryListItem>(`${tenantBase(tenantId)}/categories`, data).then((r) => r.data),

  updateCategory: (tenantId: string, id: string, data: UpdateCategoryRequest) =>
    api.put(`${tenantBase(tenantId)}/categories/${id}`, data),

  deleteCategory: (tenantId: string, id: string) =>
    api.delete(`${tenantBase(tenantId)}/categories/${id}`),

  // ── Brands ───────────────────────────────────────────────────────
  /** Markaları sayfalı döner */
  getBrandsPaged: (tenantId: string, opts?: PageQuery, signal?: AbortSignal) =>
    api
      .get<PagedResponse<BrandListItem> | BrandListItem[]>(`${tenantBase(tenantId)}/brands`, { params: pagedParams(opts), signal })
      .then((r) => normalizePaged<BrandListItem>(r.data, opts?.page ?? 1, opts?.pageSize ?? 50)),

  /** Dropdown için — tüm markalar (max 500) */
  getAllBrands: (tenantId: string) =>
    api
      .get<PagedResponse<BrandListItem> | BrandListItem[]>(`${tenantBase(tenantId)}/brands`, { params: { pageSize: 500 } })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  /** Server-side arama — debounced input için, max 50 sonuç */
  searchBrands: (tenantId: string, q: string, signal?: AbortSignal) =>
    api
      .get<PagedResponse<BrandListItem> | BrandListItem[]>(`${tenantBase(tenantId)}/brands`, {
        params: { q: q.trim() || undefined, pageSize: 50, page: 1 },
        signal,
      })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  createBrand: (tenantId: string, data: CreateBrandRequest) =>
    api.post<BrandListItem>(`${tenantBase(tenantId)}/brands`, data).then((r) => r.data),

  updateBrand: (tenantId: string, id: string, data: UpdateBrandRequest) =>
    api.put(`${tenantBase(tenantId)}/brands/${id}`, data),

  deleteBrand: (tenantId: string, id: string) =>
    api.delete(`${tenantBase(tenantId)}/brands/${id}`),

  // ── Attributes ───────────────────────────────────────────────────
  /** Nitelikleri sayfalı döner */
  getAttributesPaged: (tenantId: string, opts?: PageQuery, signal?: AbortSignal) =>
    api
      .get<PagedResponse<AttributeListItem> | AttributeListItem[]>(`${tenantBase(tenantId)}/attributes`, { params: pagedParams(opts), signal })
      .then((r) => normalizePaged<AttributeListItem>(r.data, opts?.page ?? 1, opts?.pageSize ?? 50)),

  /** Dropdown için — tüm nitelikler (max 500) */
  getAllAttributes: (tenantId: string, signal?: AbortSignal) =>
    api
      .get<PagedResponse<AttributeListItem> | AttributeListItem[]>(`${tenantBase(tenantId)}/attributes`, { params: { pageSize: 500 }, signal })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  getAttribute: (tenantId: string, id: string) =>
    api.get<AttributeDetail>(`${tenantBase(tenantId)}/attributes/${id}`).then((r) => r.data),

  createAttribute: (tenantId: string, data: CreateAttributeRequest) =>
    api.post<AttributeListItem>(`${tenantBase(tenantId)}/attributes`, data).then((r) => r.data),

  updateAttribute: (tenantId: string, id: string, data: UpdateAttributeRequest) =>
    api.put(`${tenantBase(tenantId)}/attributes/${id}`, data),

  deleteAttribute: (tenantId: string, id: string) =>
    api.delete(`${tenantBase(tenantId)}/attributes/${id}`),

  getAttributeValues: (
    tenantId: string,
    attrId: string,
    opts?: AttributeValuesQuery,
    signal?: AbortSignal,
  ): Promise<AttributeValuesResponse> => {
    const params: Record<string, string | number> = {};
    if (opts?.q) params.q = opts.q;
    if (opts?.page && opts.page > 0) params.page = opts.page;
    if (opts?.pageSize && opts.pageSize > 0) params.pageSize = opts.pageSize;
    return api
      .get<AttributeValuesResponse>(`${tenantBase(tenantId)}/attributes/${attrId}/values`, { params, signal })
      .then((r) => r.data);
  },

  addAttributeValue: (tenantId: string, attrId: string, data: AddAttributeValueRequest) =>
    api.post<AttributeValueItem>(`${tenantBase(tenantId)}/attributes/${attrId}/values`, data).then((r) => r.data),

  deleteAttributeValue: (tenantId: string, attrId: string, valueId: string) =>
    api.delete(`${tenantBase(tenantId)}/attributes/${attrId}/values/${valueId}`),

  /** Renk niteliği için TR palet ile boş ColorHex alanlarını doldurur (idempotent). */
  backfillAttributeColorHex: (tenantId: string, attrId: string) =>
    api
      .post<{ scanned: number; updated: number }>(`${tenantBase(tenantId)}/attributes/${attrId}/values/backfill-color-hex`)
      .then((r) => r.data),
};
